import React from 'react';
import {
  Avatar,
  createTheme,
  Box,
  Card,
  Button,
  TextField,
  MenuItem,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OrderDateInfo from './components/OrderDateInfo';
import { format } from 'date-fns';
import Client from '../Client';
import {
  locationsList,
  ordersList
} from './simpleBiReportsApi';

let theme = createTheme();

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function addZero(i) {
  if (i < 10) {
    i = "0" + i;
  }

  return i;
}

function isValidDateValue(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

const deliveryTypes = [
  { iref: 9, name: 'All' },
  { iref: 0, name: 'Pickup' },
  { iref: 3, name: 'Glovo' },
  { iref: 4, name: 'Tazz' },
  { iref: 5, name: 'Bolt' },
  { iref: 10, name: 'App Clients' }
];

const printTypes = [
  { iref: 9, name: 'All' },
  { iref: 0, name: 'False' },
  { iref: 1, name: 'True' }
];

const invoiceTypes = [
  { iref: 9, name: 'All' },
  { iref: 1, name: 'False' },
  { iref: 0, name: 'True' }
];

let d = new Date();
let today = format(d, 'yyyy-MM-dd');

const objectToCsv = function (data) {
  const csvRows = [];

  if (!data || !data.length) {
    return "No data";
  }

  const headers = Object.keys(data[0]).filter(header => header !== 'deliveredOn');
  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      return `"${String(val).replace(/"/g, '""')}"`;
    });

    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

class OverviewOrders extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      page: 0,
      dtype: 9,
      dtypeName: "All",
      invoiced: 9,
      invoicedName: "All",
      printed: 9,
      printedName: "All",
      rowsPerPage: 10,
      date: today,
      location: 0,
      locationName: "All locations",
      locations: [],
      paytype: 9,
      filterData: {},
      orders_list: { result: [] }
    };

    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.addDate = this.addDate.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.selectDelivery = this.selectDelivery.bind(this);
    this.selectPrinted = this.selectPrinted.bind(this);
    this.selectInvoiced = this.selectInvoiced.bind(this);
    this.selectPay = this.selectPay.bind(this);

    this.queryData = {};
  }

  componentDidMount() {
    this.getData();
  }

  getData() {
    const safeDate = isValidDateValue(this.state.date) ? this.state.date : today;

    const queryData = {
      location: this.state.location,
      date: safeDate,
      dType: this.state.dtype,
      printed: this.state.printed,
      invoiced: this.state.invoiced,
      payType: this.state.paytype,
    };

    const data = ordersList(this.state.client, queryData);

    if (!data || !Array.isArray(data?.result)) {
      console.error("Orders API unavailable", {
        endpoint: "ordersList",
        payload: queryData,
        response: data
      });

      this.setState({
        orders_list: { result: [] },
        filterData: queryData,
        date: safeDate,
        page: 0
      });

      return;
    }

    this.setState({
      orders_list: data,
      filterData: queryData,
      date: safeDate
    });

    let locations = locationsList(this.state.client);

    if (locations && Array.isArray(locations.locations)) {
      this.setState({ locations: locations.locations });
    }
  }

  doSync() {
    this.getData();
    this.setState({ page: 0 });
  }

  addDate = (event) => {
    const value = event.target.value;
    const safeDate = isValidDateValue(value) ? value : today;

    this.setState(
      { date: safeDate },
      function () {
        this.doSync();
      }
    );
  }

  selectLocation = (event) => {
    const value = event.target.value;

    const locationObj = this.state.locations.find(
      (o) => String(o.iref) === String(value)
    );

    this.setState(
      {
        location: value,
        locationName: locationObj || { name: "All locations" }
      },
      () => {
        this.doSync();
      }
    );
  };

  selectPay = (event) => {
    this.setState({ paytype: Number(event.target.value) }, () => this.doSync());
  };

  selectDelivery = (event) => {
    const value = Number(event.target.value);
    const dtypeName = deliveryTypes.find(o => o.iref === value)?.name || "All";

    this.setState({ dtype: value, dtypeName }, () => this.doSync());
  };

  selectPrinted = (event) => {
    const value = Number(event.target.value);
    const printedName = printTypes.find(o => o.iref === value)?.name || "All";

    this.setState({ printed: value, printedName }, () => this.doSync());
  };

  selectInvoiced = (event) => {
    const value = Number(event.target.value);
    const invoicedName = invoiceTypes.find(o => o.iref === value)?.name || "All";

    this.setState({ invoiced: value, invoicedName }, () => this.doSync());
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: +event.target.value,
      page: 0
    });
  }

  onDownload = () => {
    const rows = this.state.orders_list?.result || [];
    const locName = this.state.locationName?.name || "All locations";
    const dtypeName = this.state.dtypeName || "All";
    const safeDate = isValidDateValue(this.state.date) ? this.state.date : today;

    const csvHeader = `Orders List
Date: ${safeDate}
Location: ${locName}
Delivery: ${dtypeName}`;

    const csvBody = rows.length ? objectToCsv(rows) : "No data";
    const csv = "\uFEFF" + csvHeader + "\n" + csvBody;

    const link = document.createElement("a");
    link.download = `ordersList_${safeDate}_${locName}.csv`;
    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  render() {
    let locations = [{ iref: 0, name: "All locations" }];

    if (this.state.locations) {
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];

        if (fData && fData.iref !== undefined) {
          let myData = { iref: fData.iref, name: fData.name };
          locations.push(myData);
        }
      });
    }

    let ordersList = [];
    const src = this.state.orders_list?.result;

    if (Array.isArray(src)) {
      for (const fData of src) {
        const created = fData?.createdOn ? new Date(fData.createdOn) : null;
        const payments = Array.isArray(fData?.transaction) ? fData.transaction.join(", ") : "";

        ordersList.push({
          id: fData?.iref ?? "-",
          location: fData?.pos__location__name ?? "",
          ZNumber: fData?.ZNumber ?? "",
          receiptNumber: fData?.receiptNumber ?? "",
          createdOn: created,
          createdOnRaw: fData?.createdOn,
          deliveredOn: fData?.deliveredOn,
          invoiceNo: fData?.invoice__serial ?? "",
          value: thousandsSeparator(parseInt(fData?.totalAmount || 0, 10)),
          transaction: payments
        });
      }
    }

    const pagedOrders = ordersList.slice(
      this.state.page * this.state.rowsPerPage,
      this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
    );

    return (
      <Card
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          height: {
            xs: "calc(100vh - 92px)",
            md: "calc(100vh - 104px)"
          },
          minHeight: {
            xs: 500,
            sm: 540
          },
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Filters */}
        <Box
          sx={{
            px: {
              xs: 1,
              sm: 1.5
            },
            py: {
              xs: 0.75,
              sm: 1
            },
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: {
              xs: 1,
              sm: 1.25
            },
            overflowX: 'auto',
            overflowY: 'hidden',
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": {
              height: "4px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.22)",
              borderRadius: "4px"
            }
          }}
        >
          <TextField
            id="standard-select-location"
            select
            label="Location"
            value={this.state.location}
            variant="standard"
            size="small"
            sx={{
              flex: "0 0 auto",
              width: {
                xs: 150,
                sm: 165
              },
              "& .MuiInputBase-input": {
                fontSize: {
                  xs: "14px",
                  sm: "14px"
                }
              },
              "& .MuiInputLabel-root": {
                fontSize: {
                  xs: "13px",
                  sm: "13px"
                }
              }
            }}
            onChange={this.selectLocation}
          >
            {locations.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="date"
            label="Date"
            type="date"
            variant="standard"
            value={isValidDateValue(this.state.date) ? this.state.date : today}
            size="small"
            sx={{
              flex: "0 0 auto",
              width: {
                xs: 128,
                sm: 135
              },
              "& .MuiInputBase-input": {
                fontSize: {
                  xs: "14px",
                  sm: "14px"
                }
              },
              "& .MuiInputLabel-root": {
                fontSize: {
                  xs: "13px",
                  sm: "13px"
                }
              }
            }}
            InputLabelProps={{
              shrink: true,
              sx: { color: "#5b5b5b" }
            }}
            InputProps={{
              sx: { color: "#5b5b5b" }
            }}
            inputProps={{
              min: "2024-01-01",
              title: "Orders are filtered by document date.",
              style: {
                color: "#5b5b5b",
                paddingRight: 5,
                paddingLeft: 5
              }
            }}
            onChange={this.addDate}
          />

          <TextField
            id="standard-select-dType"
            select
            label="Delivery"
            value={this.state.dtype}
            variant="standard"
            size="small"
            sx={this.filterSx(88, 96)}
            onChange={this.selectDelivery}
          >
            {deliveryTypes.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="standard-select-printed"
            select
            label="Printed"
            value={this.state.printed}
            variant="standard"
            size="small"
            sx={this.filterSx(82, 90)}
            onChange={this.selectPrinted}
          >
            {printTypes.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="standard-select-invoiced"
            select
            label="Invoiced"
            value={this.state.invoiced}
            variant="standard"
            size="small"
            sx={this.filterSx(88, 96)}
            onChange={this.selectInvoiced}
          >
            {invoiceTypes.map((option) => (
              <MenuItem key={option.iref} value={option.iref}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            id="standard-select-pay"
            select
            label="Paid"
            value={this.state.paytype}
            variant="standard"
            size="small"
            sx={this.filterSx(82, 90)}
            onChange={this.selectPay}
          >
            <MenuItem value={9}>All</MenuItem>
            <MenuItem value={0}>Card</MenuItem>
            <MenuItem value={1}>Cash</MenuItem>
            <MenuItem value={2}>Credit</MenuItem>
            <MenuItem value={6}>Voucher</MenuItem>
          </TextField>

          <Button
            color="inherit"
            size="small"
            onClick={this.onDownload}
            variant="contained"
            sx={{
              maxWidth: '38px',
              minWidth: '38px',
              maxHeight: '38px',
              minHeight: '38px',
              display: {
                xs: 'none',
                sm: 'none',
                md: 'inline-flex'
              },
              ml: {
                md: "auto"
              },
              flex: "0 0 auto"
            }}
          >
            <Avatar sx={{ width: 25, height: 25, bgcolor: theme.palette.success.light }}>
              <FileDownloadIcon fontSize="small" />
            </Avatar>
          </Button>
        </Box>

        {/* Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0
          }}
        >
          <TableContainer
            sx={{
              height: "100%",
              overflow: "auto"
            }}
          >
            <Table stickyHeader aria-label="orders table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={this.headerCellSx("center")}>
                    <b>OrderNo</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("center")}>
                    <b>Created</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("right")}>
                    <b>Value</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("right")}>
                    <b>Z</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("right")}>
                    <b>Receipt</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("right")}>
                    <b>Payment</b>
                  </TableCell>

                  <TableCell sx={this.headerCellSx("right")}>
                    <b>Invoice</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedOrders.map((order) => {
                  return (
                    <TableRow
                      hover
                      key={order.id}
                      tabIndex={-1}
                    >
                      <TableCell sx={this.bodyCellSx("center", false)}>
                        {order.id}
                      </TableCell>

                      <TableCell sx={this.bodyCellSx("center")}>
                        <OrderDateInfo createdOn={order.createdOnRaw} deliveredOn={order.deliveredOn}>
                          {order.createdOn ? addZero(order.createdOn.getHours()) + ":" + addZero(order.createdOn.getMinutes()) : "-"}
                        </OrderDateInfo>
                      </TableCell>

                      <TableCell sx={this.bodyCellSx("right", false)}>
                        {order.value}
                      </TableCell>

                      <TableCell sx={this.bodyCellSx("right")}>
                        {order.ZNumber}
                      </TableCell>

                      <TableCell sx={this.bodyCellSx("right")}>
                        {order.receiptNumber}
                      </TableCell>

                      <TableCell sx={this.bodyCellSx("right")}>
                        {order.transaction}
                      </TableCell>

                      <TableCell sx={this.bodyCellSx("right")}>
                        {order.invoiceNo}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, { value: ordersList.length, label: 'All' }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={ordersList.length}
          rowsPerPage={this.state.rowsPerPage}
          page={this.state.page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid rgba(0,0,0,0.08)",
            ".MuiTablePagination-toolbar": {
              minHeight: {
                xs: 46,
                sm: 52
              },
              px: {
                xs: 1,
                sm: 2
              }
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
              fontSize: {
                xs: "13px",
                sm: "13px"
              }
            },
            ".MuiTablePagination-actions button": {
              padding: {
                xs: "4px",
                sm: "8px"
              }
            }
          }}
        />
      </Card>
    );
  }

  filterSx(xsWidth, smWidth) {
    return {
      flex: "0 0 auto",
      minWidth: {
        xs: xsWidth,
        sm: smWidth
      },
      "& .MuiInputBase-input": {
        fontSize: {
          xs: "14px",
          sm: "14px"
        }
      },
      "& .MuiInputLabel-root": {
        fontSize: {
          xs: "13px",
          sm: "13px"
        }
      }
    };
  }

  headerCellSx(align) {
    return {
      padding: {
        xs: "8px 7px",
        sm: "7px"
      },
      textAlign: align,
      whiteSpace: "nowrap",
      fontSize: {
        xs: "13px",
        sm: "13px",
        md: "14px"
      },
      backgroundColor: "#f6f4ef"
    };
  }

  bodyCellSx(align, strong = false) {
    return {
      padding: {
        xs: "8px 7px",
        sm: "7px"
      },
      textAlign: align,
      whiteSpace: "nowrap",
      fontSize: {
        xs: "13.5px",
        sm: "13px",
        md: "14px"
      },
      fontWeight: strong ? 600 : 400
    };
  }
}

export default OverviewOrders;
